import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { Box, Combobox, FormControl, Text, TextFieldAppearance, TextFieldSize } from '@bechtel/gds';
import {
  FilterConditionInput,
  FilterInput,
  OperatorType,
  SearchInput,
  SearchToolCatalogResult,
  ToolCatalog,
} from '@bechtel/ht-types';
import { useEffect, useMemo, useState } from 'react';
import { SEARCH_TOOL_CATALOGS } from '~/gql/tool-catalog';
import { ToolCategoryIcon } from './styled';

interface ToolCatalogSelectorProps {
  onSelectingToolCatalog: (catalog: ToolCatalog) => void;
  selectedItem?: ToolCatalog;
  formLabel?: string;
}

const CatalogItem = ({ item }: { item: ToolCatalog }) => {
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }} gap={{ sm: '400' }}>
      <ToolCategoryIcon
        className={`icon-tools-consumables icon-${item.toolCategory?.name.toLowerCase()}`}
      ></ToolCategoryIcon>
      <Text color="primary.500" size="300">
        {item.name}
      </Text>
    </Box>
  );
};
export const ToolCatalogSelector: React.FC<ToolCatalogSelectorProps> = ({
  onSelectingToolCatalog,
  formLabel,
  selectedItem,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [selectedToolCatalog, setSelectedToolCatalog] = useState<ToolCatalog | undefined>(selectedItem);

  const filterInput: FilterInput = useMemo(() => {
    const and: FilterConditionInput[] = [];
    and.push({ field: 'isActive', operator: OperatorType.EQUAL, value: '1' }); //TODO: Introduce Enum for InventoryType
    const filter: FilterInput = {
      or: [
        { field: 'name', operator: OperatorType.LIKE, value: searchInput },
        { field: 'bscnPartNumber', operator: OperatorType.LIKE, value: searchInput },
      ],
      and: and,
    };
    return { ...filter };
  }, [searchInput]);

  const [getCatalogList, { loading, error, refetch, networkStatus, data, fetchMore }] = useLazyQuery<
    { searchToolCatalogs: SearchToolCatalogResult },
    { searchInput: SearchInput }
  >(SEARCH_TOOL_CATALOGS, {
    variables: {
      searchInput: {
        filterInput: filterInput,
        sortInput: [],
        paginationInput: {
          limit: 40,
          offset: 0,
        },
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });
  useEffect(() => {
    getCatalogList();
  }, [searchInput, getCatalogList]);

  const itemToString = (item) => {
    return item.name;
  };

  const toolCatalogList = data?.searchToolCatalogs?.items || [];

  const filteredToolCatalogs = toolCatalogList.filter((item) => {
    return item?.name?.toLowerCase().includes(searchInput.toLowerCase());
  });
  return (
    <Box style={{ width: '100%' }}>
      <FormControl label={formLabel || ''} style={{ width: '100%' }}>
        <Combobox
          placeholder="Search from catalog"
          ariaLabel="catalog-selector"
          size={TextFieldSize.MEDIUM}
          items={filteredToolCatalogs}
          appearance={TextFieldAppearance.NORMAL}
          itemString={itemToString}
          itemComponent={CatalogItem}
          selectedComponent={CatalogItem}
          selectedItem={selectedToolCatalog}
          onSelect={async (selectedItem: ToolCatalog) => {
            setSelectedToolCatalog(selectedItem);
            onSelectingToolCatalog(selectedItem);
          }}
          initialSelectedItem={selectedItem}
          term={searchInput}
          setTerm={setSearchInput}
          handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchInput(event.target.value);
          }}
          restorePreviousOnSkip
          loading={loading || networkStatus === NetworkStatus.refetch}
          messageOnNoResults="No catalog found"
        ></Combobox>
      </FormControl>
    </Box>
  );
};
